/*
 * Wellkin — Google Ads hair-loss landing page
 * Sticky CTA reveal, lead-form UX and dataLayer tracking.
 * No dependencies; the theme's own custom elements handle sliders and accordions.
 */
(function () {
  'use strict';

  var dl = function (payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  /* ---------------------------------------------------------------------
   * Sticky mobile CTA bar
   * Revealed once the hero has scrolled away so it never covers the hero CTA.
   * ------------------------------------------------------------------- */
  function initSticky() {
    var bar = document.querySelector('[data-lp-sticky]');
    if (!bar) return;

    document.body.classList.add('lp-has-sticky');

    var setHeight = function () {
      document.body.style.setProperty('--lp-sticky-height', bar.offsetHeight + 'px');
    };
    setHeight();
    window.addEventListener('resize', setHeight, { passive: true });

    var trigger = document.querySelector('[data-lp-hero]');

    // No hero on the page (section removed in the editor) — just show the bar.
    if (!trigger || !('IntersectionObserver' in window)) {
      bar.classList.add('is-visible');
      return;
    }

    new IntersectionObserver(
      function (entries) {
        bar.classList.toggle('is-visible', !entries[0].isIntersecting);
      },
      { rootMargin: '-80px 0px 0px 0px' }
    ).observe(trigger);
  }

  /* ---------------------------------------------------------------------
   * CTA click tracking + smooth scroll to the form
   * ------------------------------------------------------------------- */
  function initCtaTracking() {
    document.addEventListener('click', function (event) {
      var el = event.target.closest('[data-lp-track]');
      if (!el) return;

      dl({
        event: el.getAttribute('data-lp-track'),
        cta_location: el.getAttribute('data-lp-location') || 'unknown'
      });
    });

    // Anchor CTAs (#lp-book) scroll to the form and focus its first field.
    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[href="#lp-book"]');
      if (!link) return;

      var target = document.getElementById('lp-book');
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });

      var firstField = target.querySelector('input:not([type=hidden]):not(.lp-hp input)');
      if (firstField) {
        window.setTimeout(function () {
          firstField.focus({ preventScroll: true });
        }, 500);
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Lead form
   * ------------------------------------------------------------------- */
  function initForms() {
    var wrappers = document.querySelectorAll('[data-lp-form]');

    Array.prototype.forEach.call(wrappers, function (wrapper) {
      var form = wrapper.querySelector('form');
      if (!form) return;

      var phone = form.querySelector('input[type=tel]');

      if (phone) {
        // Strip anything that isn't a digit, +, space or hyphen as the user types.
        phone.addEventListener('input', function () {
          var cleaned = phone.value.replace(/[^\d+\s-]/g, '');
          if (cleaned !== phone.value) phone.value = cleaned;
          phone.setCustomValidity('');
        });

        phone.addEventListener('invalid', function () {
          phone.setCustomValidity(phone.getAttribute('data-lp-error') || 'Please enter a valid mobile number.');
        });
      }

      form.addEventListener('submit', function (event) {
        // Honeypot: a filled hidden field means a bot. Drop it silently.
        var hp = form.querySelector('[data-lp-hp]');
        if (hp && hp.value !== '') {
          event.preventDefault();
          return;
        }

        dl({
          event: 'lead_form_submit',
          form_location: wrapper.getAttribute('data-lp-form') || 'unknown'
        });

        // Optionally also store the lead as a Shopify customer, so it is not
        // email-only. Shopify's customer endpoint requires an email, so this is
        // skipped for phone-only leads.
        //
        // This MUST stay fire-and-forget. Calling preventDefault() and then
        // form.submit() bypasses the submit event, which is exactly where
        // Shopify's hCaptcha script injects its token — without it the real
        // submission silently fails. keepalive lets the request outlive the
        // page navigation that follows.
        var tags = wrapper.getAttribute('data-lp-save-customer');
        var emailField = form.querySelector('input[type=email]');
        var email = emailField && emailField.value.trim();

        if (!tags || !email) return;

        var nameField = form.querySelector('input[autocomplete=name]');
        var body = new FormData();
        body.append('form_type', 'customer');
        body.append('utf8', '✓');
        body.append('contact[email]', email);
        body.append('contact[tags]', tags);
        if (nameField && nameField.value) body.append('contact[first_name]', nameField.value.trim());

        try {
          fetch('/contact', {
            method: 'POST',
            body: body,
            credentials: 'same-origin',
            keepalive: true
          }).catch(function () {});
        } catch (e) {
          /* Never let lead storage interfere with the actual submission. */
        }
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Conversion firing on the successful-post render
   * The success panel is server-rendered by Liquid after Shopify's redirect,
   * so this runs exactly once per genuine submission.
   * ------------------------------------------------------------------- */
  function initConversion() {
    var panels = Array.prototype.slice.call(document.querySelectorAll('[data-lp-success]'));
    if (!panels.length) return;

    // Both forms render a success panel after Shopify's redirect — that is accurate,
    // the lead was received either way, and leaving both avoids an empty section.
    // The submitting form's id is the URL fragment, so use it to pick the single
    // panel that reports the conversion and receives focus.
    var hash = window.location.hash.replace('#', '');
    var matched = panels.filter(function (panel) {
      return panel.getAttribute('data-lp-form-id') === hash;
    });
    var success = matched.length ? matched[0] : panels[0];

    dl({
      event: 'generate_lead',
      form_location: success.getAttribute('data-lp-success') || 'unknown',
      currency: 'AED',
      value: parseFloat(success.getAttribute('data-lp-value')) || 0
    });

    // Direct Google Ads conversion, only when a send_to has been configured.
    var sendTo = success.getAttribute('data-lp-send-to');
    if (sendTo && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: sendTo,
        value: parseFloat(success.getAttribute('data-lp-value')) || 0,
        currency: 'AED'
      });
    }

    success.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /* ---------------------------------------------------------------------
   * Slider autoplay
   * Scrolls one slide-width at a time and wraps at the end. Pauses on hover,
   * on focus within, and while the tab is hidden, and respects reduced motion.
   * ------------------------------------------------------------------- */
  function initAutoplay() {
    var sliders = document.querySelectorAll('[data-lp-autoplay]');
    if (!sliders.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    Array.prototype.forEach.call(sliders, function (slider) {
      var seconds = parseFloat(slider.getAttribute('data-lp-autoplay')) || 5;
      if (slider.children.length < 2) return;

      var paused = false;
      var pause = function () { paused = true; };
      var play = function () { paused = false; };

      slider.addEventListener('mouseenter', pause);
      slider.addEventListener('mouseleave', play);
      slider.addEventListener('focusin', pause);
      slider.addEventListener('focusout', play);
      // A manual swipe should not fight the timer.
      slider.addEventListener('pointerdown', pause);
      slider.addEventListener('pointerup', play);

      window.setInterval(function () {
        if (paused || document.hidden) return;

        var step = slider.clientWidth;
        // Within a slide's width of the end: wrap back to the start.
        var atEnd = slider.scrollLeft + step >= slider.scrollWidth - 4;
        slider.scrollTo({ left: atEnd ? 0 : slider.scrollLeft + step, behavior: 'smooth' });
      }, seconds * 1000);
    });
  }

  function init() {
    initSticky();
    initCtaTracking();
    initForms();
    initConversion();
    initAutoplay();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
